import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { captureException } from '@sentry/angular';
import type { Extras } from '@sentry/core';

interface NormalizedError {
  error: Error;
  extras?: Extras;
}

const stringifyUnknown = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};

// Sentry renders non-Error values as an opaque "Object captured as exception",
// with no stack and useless grouping - so anything callers pass (usually the
// raw value from rxjs catchError) must be turned into a real Error first.
const normalizeError = (error: unknown): NormalizedError => {
  if (error instanceof Error) {
    return { error };
  }
  if (error instanceof HttpErrorResponse) {
    const normalized = new Error(error.message);
    normalized.name = 'HttpErrorResponse';
    return {
      error: normalized,
      extras: {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        response: error.error,
      },
    };
  }
  return {
    error: new Error(stringifyUnknown(error)),
    extras: { originalValue: error },
  };
};

@Injectable({
  providedIn: 'root',
})
export class LoggerService {

  public logError(error: unknown, extras?: Extras): void {
    console.error(error);
    try {
      const normalized = normalizeError(error);
      captureException(normalized.error, (scope) => {
        const combinedExtras = { ...normalized.extras, ...extras };
        if (Object.keys(combinedExtras).length > 0) {
          scope.setExtras(combinedExtras);
        }
        return scope;
      });
    } catch {
      console.error('Failed to report');
    }
  }
}
